// NativeTabBar — the bottom tab bar for the Capacitor iOS/Android app.
//
// Built from scratch for the app (NOT the web AppFooter, which is a horizontally
// scrolling carousel of 8+ links). This is a fixed iOS-style tab bar: a small
// set of primary destinations + a "More" sheet that surfaces everything else.
//
// Deliberately omitted vs the web footer:
//   • Home    — the app has no marketing homepage; it opens straight into Search
//               (and, soon, a location-aware "Near Me" landing).
//   • Follow  — a web acquisition CTA; meaningless on an already-installed app.
//               Push notifications are offered in-context + configured in Settings.
//   • States  — high value for desktop research, low value on mobile → demoted
//               into the More sheet.
//
// Aesthetic: AHP patriotic, frosted to match AhpGlobalHeader. Light mode = white
// frosted bar with red active accent; dark mode = navy frosted bar with cream
// active accent. A thin flag stripe along the top edge brackets the app against
// the header's stripe.

import { useEffect, useState } from "react";
import FlagStripe from '@/components/FlagStripe';
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/hooks/useAuth";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { useAppReviewFlags } from "@/config/appReviewFlags";
import {
  Search, Activity, TrendingUp, Users2, MoreHorizontal,
  MapPin, Globe, Heart, HeartHandshake, BookOpen, Gift, Settings, FileText,
  MessageCircle, ShoppingBag, X,
  Database, FlaskConical, Code2, Plug, Star, Globe2, Map as MapIcon,
} from "lucide-react";

interface TabDef {
  to: string;
  label: string;
  icon: React.ReactNode;
  match: (path: string) => boolean;
}

export const NativeTabBar = () => {
  const location = useLocation();
  const { isDark } = useTheme();
  const { t } = useTranslation("common");
  const { isAuthenticated } = useAuth();
  const { data: adminStatus } = useAdminStatus();
  const isAdmin = adminStatus?.isAdmin;
  const [moreOpen, setMoreOpen] = useState(false);
  // App-review gates (native build): "What's New" hidden during App Store
  // review; merch hidden until the remote flag opens the store publicly.
  const reviewFlags = useAppReviewFlags();
  const showMerch = isAdmin || reviewFlags.showMerch;

  const path = location.pathname;

  // Lock body scroll while the More sheet is open (restore on close/unmount).
  useEffect(() => {
    if (!moreOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, [moreOpen]);

  // ── Palette ────────────────────────────────────────────────────────────
  const barBg = isDark ? "rgba(10,22,40,0.86)" : "rgba(255,255,255,0.90)";
  const accent = isDark ? "#C5A55A" : "#B22234";          // active: cream / red
  const idle = isDark ? "rgba(255,255,255,0.50)" : "rgba(27,27,27,0.62)";
  const border = isDark ? "rgba(197,165,90,0.20)" : "rgba(0,40,104,0.10)";

  // ── Primary tabs (4) + More ──────────────────────────────────────────────
  const primary: TabDef[] = [
    { to: "/search", label: t("nav.search"), icon: <Search />, match: (p) => p === "/search" || p === "/" },
    { to: "/scenarios", label: t("nav.scenarios"), icon: <Activity />, match: (p) => p.startsWith("/scenarios") },
    { to: "/hospital-rankings", label: t("nav.ranks"), icon: <TrendingUp />, match: (p) => p.startsWith("/hospital-rankings") },
    { to: "/near-me", label: "Near Me", icon: <MapPin />, match: (p) => p.startsWith("/near-me") },
  ];

  // ── "See all" sheet — every other destination ────────────────────────────
  const moreItems: TabDef[] = [
    // Near Me is now a primary tab; Family moved here from the primary row.
    { to: "/family", label: "Family", icon: <Users2 />, match: (p) => p.startsWith("/family") },
    { to: "/followed", label: "Followed", icon: <Star />, match: (p) => p.startsWith("/followed") },
    { to: "/map", label: "Map", icon: <MapIcon />, match: (p) => p.startsWith("/map") },
    { to: "/state-comparison", label: "States", icon: <Globe />, match: (p) => p.startsWith("/state-comparison") },
    { to: "/international", label: "International", icon: <Globe2 />, match: (p) => p.startsWith("/international") },
    { to: "/connect-to-care", label: "Connect to Care", icon: <Heart />, match: (p) => p.startsWith("/connect-to-care") },
    { to: "/about/dignity", label: "Dignity", icon: <HeartHandshake />, match: (p) => p.startsWith("/about/dignity") },
    ...((reviewFlags.showStories || isAdmin) ? [{ to: "/stories", label: "Stories", icon: <BookOpen />, match: (p: string) => p.startsWith("/stories") }] : []),
    ...(reviewFlags.showPayments ? [{ to: "/donate", label: "Support", icon: <Gift />, match: (p: string) => p.startsWith("/donate") }] : []),
    ...(reviewFlags.showReleaseNotes ? [{ to: "/release-notes", label: "What's New", icon: <FileText />, match: (p: string) => p.startsWith("/release-notes") }] : []),
    { to: "/data-sources", label: "Data Sources", icon: <Database />, match: (p) => p.startsWith("/data-sources") },
    { to: "/data-methodology", label: "Methodology", icon: <FlaskConical />, match: (p) => p.startsWith("/data-methodology") },
    { to: "/api", label: "API", icon: <Code2 />, match: (p) => p === "/api" || p.startsWith("/api/") },
    { to: "/mcp", label: "MCP", icon: <Plug />, match: (p) => p.startsWith("/mcp") },
    { to: "/settings", label: t("nav.settings"), icon: <Settings />, match: (p) => p.startsWith("/settings") },
    { to: "/chat", label: "Connect AI", icon: <MessageCircle />, match: (p: string) => p.startsWith("/chat") },
    ...(showMerch ? [{ to: "/merch", label: t("nav.merch"), icon: <ShoppingBag />, match: (p: string) => p.startsWith("/merch") }] : []),
  ];

  const moreActive = moreItems.some((m) => m.match(path));

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    paddingTop: 8,
    paddingBottom: 4,
    color: active ? accent : idle,
    textDecoration: "none",
    transition: "color 0.15s ease",
  });
  const iconWrap = { width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" } as React.CSSProperties;
  const labelStyle = { fontSize: 10.5, fontWeight: 600, letterSpacing: 0.1, fontFamily: "'Instrument Sans', system-ui, sans-serif" } as React.CSSProperties;

  return (
    <>
      {/* ── More sheet ────────────────────────────────────────────────────── */}
      {moreOpen && (
        <div
          onClick={() => setMoreOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 10001, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="All sections"
            style={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 10002,
              background: isDark ? "#0a1628" : "#ffffff",
              borderTopLeftRadius: 20, borderTopRightRadius: 20,
              borderTop: `1px solid ${border}`,
              boxShadow: "0 -8px 30px rgba(0,0,0,0.25)",
              paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
            }}
          >
            {/* flag stripe + grabber + header */}
            <FlagStripe height={4} style={{ borderTopLeftRadius: 20, borderTopRightRadius: 20 }} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px 6px" }}>
              <span style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 22, color: isDark ? "#fff" : "#002868" }}>
                All sections
              </span>
              <button onClick={() => setMoreOpen(false)} aria-label="Close" style={{ background: "none", border: "none", color: idle, padding: 4, cursor: "pointer" }}>
                <X />
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4, padding: "8px 12px 4px", maxHeight: "80dvh", overflowY: "auto" }}>
              {moreItems.map((m) => {
                const active = m.match(path);
                return (
                  <Link
                    key={m.to}
                    to={m.to}
                    onClick={() => setMoreOpen(false)}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                      padding: "14px 4px", borderRadius: 14, textDecoration: "none",
                      color: active ? accent : (isDark ? "rgba(255,255,255,0.78)" : "#1B1B1B"),
                      background: active ? (isDark ? "rgba(197,165,90,0.12)" : "rgba(178,34,52,0.08)") : "transparent",
                    }}
                  >
                    <span style={{ width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center" }}>{m.icon}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, textAlign: "center", lineHeight: 1.15, fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>{m.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab bar ───────────────────────────────────────────────────────── */}
      <nav
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 10000,
          backgroundColor: barBg,
          backdropFilter: "saturate(180%) blur(20px)",
          WebkitBackdropFilter: "saturate(180%) blur(20px)",
          borderTop: `1px solid ${border}`,
          // The frosted bar background already runs to the screen bottom, so we
          // don't need the FULL home-indicator inset as empty padding — just
          // enough clearance that labels aren't under the indicator. Trims ~26px
          // of reclaimable dead space at the bottom (deepened from −16px per
          // Master 2026-07-11; labels still clear the indicator).
          paddingBottom: "max(env(safe-area-inset-bottom, 0px) - 26px, 6px)",
        }}
      >
        {/* flag stripe bracketing the app against the header's stripe */}
        <FlagStripe height={3} />
        <div style={{ display: "flex", alignItems: "stretch", paddingLeft: 4, paddingRight: 4 }}>
          {primary.map((tab) => {
            const active = tab.match(path);
            return (
              <Link key={tab.to} to={tab.to} style={tabStyle(active)}>
                <span style={iconWrap}>{tab.icon}</span>
                <span style={labelStyle}>{tab.label}</span>
              </Link>
            );
          })}
          <button
            onClick={() => setMoreOpen(true)}
            aria-label="More sections"
            style={{ ...tabStyle(moreActive), background: "none", border: "none", cursor: "pointer" }}
          >
            <span style={iconWrap}><MoreHorizontal /></span>
            <span style={labelStyle}>More</span>
          </button>
        </div>
      </nav>
    </>
  );
};
